import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Gift, HandCoins, Link2, LogOut, Menu, Package, Shield, ShoppingBag, Star, Trophy, X } from "lucide-react";
import { api } from "@/lib/api";
import { useCreatorCode } from "@/lib/creatorLink";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useAutoRefresh } from "@/lib/live";

const LINKS = [
  { to: "/catalogo", label: "Catalogo" },
  { to: "/bundle", label: "Crea il Bundle" },
  { to: "/deck-builder", label: "Deck Builder" },
  { to: "/tornei", label: "Tornei" },
  { to: "/wanted-season", label: "Wanted Season" },
  { to: "/eventi", label: "Eventi" },
  { to: "/fedelta", label: "Programma Fedeltà" },
  { to: "/servizi", label: "I Nostri Servizi" },
  { to: "/chi-siamo", label: "Chi Siamo" },
  { to: "/contatti", label: "Contatti" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const creatorCode = useCreatorCode();
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const [drop, setDrop] = useState(false);
  const [hasWanted, setHasWanted] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  // La voce "I Nostri Wanted" compare solo se la buylist ha almeno una carta attiva
  useEffect(() => {
    api.get("/wanted", { params: { page: 1 } }).then((r) => setHasWanted((r.data.total || 0) > 0)).catch(() => {});
  }, [loc]);

  const links = hasWanted
    ? [...LINKS.slice(0, 1), { to: "/wanted", label: "I Nostri Wanted" }, ...LINKS.slice(1)]
    : LINKS;

  useEffect(() => {
    setMobile(false);
    setDrop(false);
    setBellOpen(false);
  }, [loc]);

  const [notifs, setNotifs] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);

  const refreshNotifs = useCallback(() => {
    if (user) api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    refreshNotifs();
  }, [refreshNotifs, loc]);
  useAutoRefresh(refreshNotifs, 60000); // nuove notifiche (tornei, ordini) senza reload

  const unread = notifs.filter((n) => !n.read).length;
  const toggleBell = async () => {
    setDrop(false);
    setBellOpen(!bellOpen);
    if (!bellOpen && unread > 0) {
      await api.post("/notifications/read").catch(() => {});
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };
  const toggleDrop = () => {
    setBellOpen(false);
    setDrop(!drop);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#050505]/85 border-b border-white/10">
      <div className="h-20 px-4 sm:px-8 flex items-center justify-between max-w-[1600px] mx-auto">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src="/logo-rosa.png" alt="Rosa dei venti WANTED" className="h-10 w-10 object-contain transition-transform duration-500 group-hover:rotate-180" />
          <img src="/logo-scritta.png" alt="WANTED" className="h-5 sm:h-6 w-auto object-contain group-hover:brightness-110 transition" />
        </Link>

        <nav className="hidden xl:flex items-center gap-4">
          {links.map((l) => {
            const active = loc.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                aria-current={active ? "page" : undefined}
                className={`relative text-[11px] font-medium tracking-wide uppercase transition-colors whitespace-nowrap ${active ? "text-red-500" : "text-zinc-400 hover:text-white"}`}
              >
                {l.label}
                {active && <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-red-500" aria-hidden />}
              </Link>
            );
          })}
          {["admin","master_admin"].includes(user?.role) && (
            <Link to="/admin" data-testid="nav-link-admin" className="text-sm font-medium text-red-500 hover:text-red-400 tracking-wide uppercase transition-colors flex items-center gap-1.5">
              <Shield size={14} /> Admin
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {creatorCode && (
            <span
              data-testid="nav-creator-badge"
              title={`Modalità link attiva: ogni link che condividi porta il tuo codice ?ref=${creatorCode}`}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-[10px] font-mono font-bold uppercase tracking-widest text-red-400"
            >
              <Link2 size={11} /> Link attivo
            </span>
          )}
          {user && (
            <div className="relative hidden sm:block">
              <button
                data-testid="nav-points-badge"
                onClick={toggleDrop}
                className="flex items-center gap-2 border border-red-600/40 bg-red-600/10 rounded-full px-4 py-1.5 hover:bg-red-600/20 transition-colors"
              >
                <span data-testid="nav-user-label" className="text-xs font-display font-bold uppercase tracking-wide text-white hidden md:inline max-w-28 truncate">
                  {user.nav_label === "nickname" && user.nickname ? user.nickname : (user.name || "").split(" ")[0]}
                </span>
                <Star size={13} className="text-red-500 fill-red-500" />
                <span className="font-mono text-sm font-semibold text-white">{user.points_balance}</span>
                <span className="text-[10px] font-mono uppercase tracking-wider text-red-400">PT</span>
              </button>
              <AnimatePresence>
                {drop && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="absolute right-0 mt-2 w-56 rounded-lg border border-white/10 bg-[#0D0D0E] shadow-2xl p-2 z-50"
                  >
                    <div className="px-3 py-2 border-b border-white/10">
                      <p className="text-sm font-semibold text-white">{user.name}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </div>
                    <Link to="/profilo" data-testid="nav-profile-link" className="block px-3 py-2 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded">
                      Area Personale
                    </Link>
                    <Link to="/ordini" data-testid="nav-orders-link" className="block px-3 py-2 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded">
                      I miei ordini
                    </Link>
                    <button
                      data-testid="nav-logout-button"
                      onClick={async () => { await logout(); nav("/"); }}
                      className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-white/5 rounded flex items-center gap-2"
                    >
                      <LogOut size={14} /> Esci
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {!user && user !== undefined && (
            <Link
              to="/login"
              data-testid="nav-login-button"
              className="hidden sm:inline-flex text-sm font-semibold uppercase tracking-wide border border-white/20 hover:border-red-600 hover:text-red-500 rounded-full px-5 py-2 transition-colors"
            >
              Accedi
            </Link>
          )}

          {user && (
            <div className="relative">
              <button
                data-testid="nav-notifications-button"
                onClick={toggleBell}
                className="relative p-2.5 rounded-full border border-white/15 hover:border-red-600/60 transition-colors"
                aria-label="Notifiche"
              >
                <Bell size={18} />
                {unread > 0 && (
                  <span data-testid="nav-notifications-badge" className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-mono font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </button>
              <AnimatePresence>
                {bellOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="fixed left-3 right-3 top-20 w-auto sm:absolute sm:top-auto sm:left-auto sm:right-0 sm:mt-2 sm:w-96 rounded-lg border border-white/10 bg-[#0D0D0E] shadow-2xl p-2 z-50"
                  >
                    <p className="px-3 py-2 text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10">Notifiche</p>
                    {notifs.length === 0 ? (
                      <p className="px-3 py-4 text-xs text-zinc-500">Nessuna notifica.</p>
                    ) : (
                      <ul className="max-h-80 overflow-y-auto">
                        {notifs.map((n, i) => {
                          const t = `${n.title} ${n.message}`.toLowerCase();
                          const meta = t.includes("proposta") || t.includes("wanted")
                            ? { Icon: HandCoins, to: n.link || (["admin","master_admin"].includes(user?.role) ? "/admin?tab=acquisti" : "/profilo?sezione=proposte"), tint: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" }
                            : t.includes("punt") || t.includes("forziere") || t.includes("credit")
                            ? { Icon: Star, to: n.link || "/programma-fedelta", tint: "text-red-400 border-red-600/30 bg-red-600/10" }
                            : t.includes("torneo") || t.includes("waitlist") || t.includes("iscriz")
                            ? { Icon: Trophy, to: n.link || "/tornei", tint: "text-amber-400 border-amber-500/30 bg-amber-500/10" }
                            : t.includes("premio") || t.includes("reward")
                            ? { Icon: Gift, to: n.link || "/programma-fedelta", tint: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" }
                            : { Icon: Package, to: n.link || "/profilo", tint: "text-zinc-300 border-white/15 bg-white/5" };
                          return (
                            <li key={i} data-testid={`notification-${i}`} className="px-3 py-3 border-b border-white/5 last:border-0 flex items-start gap-3">
                              <span className={`mt-0.5 p-2 rounded-full border shrink-0 ${meta.tint}`}>
                                <meta.Icon size={13} />
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-white">{n.title}</p>
                                <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{n.message}</p>
                                <div className="flex items-center justify-between gap-3 mt-1.5">
                                  <p className="text-[10px] font-mono text-zinc-600">{new Date(n.created_at).toLocaleDateString("it-IT")}</p>
                                  <Link to={meta.to} data-testid={`notification-open-${i}`} onClick={() => setBellOpen(false)} className="text-[10px] font-display font-bold uppercase tracking-wide text-red-400 hover:text-red-300 transition-colors">
                                    Apri →
                                  </Link>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <button
            data-testid="nav-cart-button"
            onClick={() => setOpen(true)}
            className="relative p-2.5 rounded-full border border-white/15 hover:border-red-600/60 transition-colors"
            aria-label="Apri carrello"
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-mono font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <button data-testid="nav-mobile-menu-button" onClick={() => setMobile(!mobile)} className="xl:hidden p-2" aria-label="Menu">
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {(drop || bellOpen) && (
        <button aria-hidden tabIndex={-1} onClick={() => { setDrop(false); setBellOpen(false); }} className="fixed inset-0 z-40 cursor-default" />
      )}

      <AnimatePresence>
        {mobile && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="xl:hidden border-t border-white/10 bg-[#050505]/95 overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((l) => {
                const active = loc.pathname === l.to;
                return (
                  <Link key={l.to} to={l.to} className={`py-3 text-sm uppercase tracking-wide border-b border-white/5 flex items-center gap-2 ${active ? "text-red-500 font-bold" : "text-zinc-300"}`}>
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden />}
                    {l.label}
                  </Link>
                );
              })}
              {user && (
                <Link to="/profilo" data-testid="nav-mobile-profile-link" className="py-3 text-sm uppercase tracking-wide text-red-400 border-b border-white/5 flex items-center gap-2">
                  <Star size={14} className="fill-red-500 text-red-500" /> Area Personale ({user.points_balance} PT)
                </Link>
              )}
              {user && (
                <Link to="/ordini" className="py-3 text-sm uppercase tracking-wide text-zinc-300 border-b border-white/5">I miei ordini</Link>
              )}
              {["admin","master_admin"].includes(user?.role) && (
                <Link to="/admin" className="py-3 text-sm uppercase tracking-wide text-red-500 border-b border-white/5">Admin</Link>
              )}
              {!user && user !== undefined && (
                <Link to="/login" className="py-3 text-sm uppercase tracking-wide text-red-500">Accedi</Link>
              )}
              {user && (
                <button onClick={async () => { await logout(); nav("/"); }} className="py-3 text-left text-sm uppercase tracking-wide text-red-400">
                  Esci
                </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
